import { FormSectionHandler } from '../entity.handler';
import { ClientResponse, ClientUpdateRequest, Shift } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';

interface EditorValues {
    dailyShift?: ListFieldValue<Shift>[];
}

export class ClientShiftSectionHandler implements FormSectionHandler<ClientResponse, EditorValues> {

    type = 'form';
    icon = 'business-time';
    bulk = false;
    readonly = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    constructor(
        public admin: AdminEditorService
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.CLIENT.SHIFT.${key}`, values);
    }

    applyUpdate(entity: ClientResponse, payload: ClientUpdateRequest): Promise<ClientResponse> {
        return this.admin.applyUpdate(api => api.accounts.updateClient(entity.id, payload)).then(client => {
            this.admin.clientUpdated(client);
            return client;
        });
    }

    mapEntityToEditor(entity: ClientResponse): Promise<EditorValues> {
        return Promise.resolve({
            dailyShift: (entity.shiftPattern && entity.shiftPattern.dailyShift || []).map(x => this.shiftToFormListItem(x)),
        });
    }

    mapEditorToEntity(entity: ClientUpdateRequest, values: EditorValues): Promise<any> {
        entity.shiftPattern = { dailyShift: (values.dailyShift || []).map(x => x.data) };
        return Promise.resolve(entity);
    }

    async getFields(_entity: ClientResponse): Promise<FormBuilderField[]> {
        return [{
            id: 'dailyShift',
            title: this.translate('FIELDS.DAILY_SHIFT'),
            type: 'list',
            required: false,
            actions: [
                {
                    id: 'delete', click: async (_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<Shift>) => {
                        const items = values.dailyShift;
                        const index = items.findIndex(x => x.id === item.id);
                        items.splice(index, 1);
                    },
                },
                {
                    id: 'edit', click: async (_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<Shift>) => {
                        const shift = await this.openEditShowModal(item.data);
                        if (shift) {
                            const items = values.dailyShift;
                            const index = items.findIndex(x => x.id === item.id);
                            items[index] = shift;
                        }
                    },
                },
                {
                    name: this.admin.i18n.instant('SHARED.ADD'), click: async (_action: string, _field: FormBuilderField, values: EditorValues, _item: ListFieldValue<Shift>) => {
                        const shifts = values.dailyShift;
                        const prevShift = (shifts || [])[shifts.length - 1];
                        const times = this.getStartEndTimes(prevShift && prevShift.data);
                        const shift = await this.openEditShowModal({
                            start: times.start,
                            end: times.end,
                            id: uuid.v4(),
                            name: null,
                        });
                        if (shift) {
                            shifts.push(shift);
                        }
                    },
                },
            ],
            getText(_field: FormBuilderField, values: EditorValues): string {
                return (values.dailyShift || []).map(x => x.name).join(', ');
            },
        }];
    }

    async openEditShowModal(values: Shift): Promise<ListFieldValue<Shift>> {
        const trimTime = time => {
            const [HH, mm] = time.split(':');
            return `${HH}:${mm}`;
        };
        const res = await this.admin.modal.form({
            groups: [{
                name: this.translate('MODAL.TITLE'),
                description: values.name ? this.translate('MODAL.EDIT_DESC') : this.translate('MODAL.ADD_DESC'),
                fields: [
                    { id: 'name', title: this.translate('MODAL.FIELDS.NAME'), type: 'text', required: true },
                    { id: 'start', title: this.translate('MODAL.FIELDS.START'), type: 'time', required: true },
                    { id: 'end', title: this.translate('MODAL.FIELDS.END'), type: 'time', required: true },
                ],
            }],
        }, values);

        if (res) {
            res.start = trimTime(res.start);
            res.end = trimTime(res.end);
        }

        return res && this.shiftToFormListItem(res);
    }

    // set the start and end times from the previous end time as an 9 hour shift
    getStartEndTimes(prevShift: Shift): { start: string, end: string } {
        return prevShift ? {
            start: prevShift.end,
            end: prevShift.end.split(':').reduce((total, x) => {
                if (!total) {
                    const prev = parseInt(x, 10);
                    // add 9 hours to the previous item's end time, but only if it will fall into 24 hours
                    return prev < 16 ? (prev + 9).toString() : '00';
                } else {
                    // add minutes and/or seconds to the total without changing it
                    return total + ':' + x;
                }
            }, ''),
        } : { start: '08:00', end: '17:00' };
    }

    private shiftToFormListItem(shift: Shift): ListFieldValue<Shift> {
        return {
            id: shift.id,
            name: shift.name,
            description: shift.start + ' - ' + shift.end,
            icon: this.icon,
            data: shift,
        };
    }
}
